import {createSelector} from 'reselect';
import _ from 'lodash';
import {getGroupAbleName} from "../../utilities";

const selectProjectScope = (state) => state.projectScope;

const selectFeatureGroups = (state) => state.projectScope.featureGroups;

const selectPendingFeatureGroups = (state) => state.newFeatureGroup.newFeatureGroups;

const selectAllFeatureGroups = (state) => _.union(selectFeatureGroups(state), selectPendingFeatureGroups(state));

const selectFeatures = (state) => state.projectScope.features;

const selectComparableSearchTerm = (state) => _.trim(state.search.term).toLowerCase();

const selectActiveFeatureGroupName = (state) => state.projectScope.activeFeatureGroupName;

const selectFabricatorInclusionPreset = (state) => state.featureInclusion.fabricatorInclusionPreset;

const selectFabricatorSubscriptions = (state) => state.projectScope.fabricatorSubscriptions;

export const hasSubFor = (state) => {
  let subscriptions = selectFabricatorSubscriptions(state);
  return (service) => (subscriptions.length === 0 || _.find(subscriptions, s => s.service === service) !== undefined);
}

export const getAssociatedFeaturesByNakedNameFromState = (state, nakedName) => {
  return getAssociatedFeaturesByNakedName(selectProjectScope(state), nakedName)
};

export const getFeaturesWithSameNameFromState = (state, nakedName) => {
  const {featureDefinitions} = selectProjectScope(state);
  const groupedName = getGroupAbleName(nakedName);
  return _.filter(featureDefinitions, fd => fd.nakedName && getGroupAbleName(fd.nakedName) === groupedName);
};

export const getAssociatedFeaturesByNakedName = ({featureDefinitions, fabricator, fabFeatMap}, nakedName) => {
  return _.filter(featureDefinitions,
    // fd => fabFeatMap[fd.id] && fabFeatMap[fd.id].fabricator_id === fabricator.id && fd.nakedName === nakedName);
    fd => fd.nakedName === nakedName);
}

export const selectSearchTerm = (state) => state.search.term;

export const doesFeatureGroupHaveFeaturesWithLayersOfType = (fg, layersOfType) => {
  if (!fg.isComplex) {
    return _.some(fg.features, feat => feat.hasLayers && feat.hasLayers[layersOfType])
  }

  return _.some(fg.subGroups, sg => doesFeatureGroupHaveFeaturesWithLayersOfType(sg, layersOfType));
}

const isFeatEnabledFor = (feat, ofType) => {
  switch(ofType) {
    case "kitchen":
      return (feat && ((feat.fabricatorData && feat.fabricatorData.hideFromKitchenVisualizer) || !feat.hideFromKitchenVisualizer));
    case "bathroom":
      return (feat && ((feat.fabricatorData && feat.fabricatorData.hideFromBathroomVisualizer) || !feat.hideFromBathroomVisualizer));
  }
  return false;
}

export const hasFeaturesWithEnabledFor = (fg, ofType) => {
  if(!fg.isComplex) {
    return _.some(fg.features, feat => isFeatEnabledFor(feat, ofType));
  }
  return _.some(fg.subGroups, sg => hasFeaturesWithEnabledFor(sg, ofType));
}

export const getFilteredFeatureGroups = createSelector(
  [selectFeatureGroups, selectComparableSearchTerm, selectFabricatorInclusionPreset],
  (featureGroups, searchTerm, preset) => {
    if ((searchTerm.length > 0 || preset) && _.some(featureGroups)) {
      featureGroups = featureGroups.filter(fg => {
        let filter = true;
        if (searchTerm.length > 0) {
          filter = fg.name.toLowerCase().includes(searchTerm);
        }
        if (preset && preset.featureGroups) {
          let allowedFeatureGroup = false;
          for (let i = 0; i < preset.featureGroups.length; i++) {
            allowedFeatureGroup |= _.isEqual(fg.name.toLowerCase(), preset.featureGroups[i].featureGroup.toLowerCase());
          }
          filter &= allowedFeatureGroup;
        }
        return filter;
      });
    }
    return featureGroups;
  }
);

export const getUnfilteredFeatureGroups = createSelector(
  [selectFeatureGroups, selectFabricatorInclusionPreset],
  (featureGroups, preset) => {
    if (preset && _.some(featureGroups)) {
      featureGroups = featureGroups.filter(fg => {
        let filter = true;
        if (preset && preset.featureGroups) {
          let allowedFeatureGroup = false;
          for (let i = 0; i < preset.featureGroups.length; i++) {
            allowedFeatureGroup |= _.isEqual(fg.name.toLowerCase(), preset.featureGroups[i].featureGroup.toLowerCase());
          }
          filter &= allowedFeatureGroup;
        }
        return filter;
      });
    }
    return featureGroups;
  }
);

export const getActiveFeatureGroup = createSelector(
  [selectActiveFeatureGroupName, selectAllFeatureGroups],
  (activeFeatureGroup, featureGroups) => {
    if (activeFeatureGroup && _.some(featureGroups))
      return _.find(featureGroups, fg => fg.name === activeFeatureGroup);
    return null;
  }
)

export const getActiveFeatureGroupEnabledFeatures = createSelector(
  [getActiveFeatureGroup, selectFabricatorInclusionPreset],
  (activeFeatureGroup, preset) => {
    if (!activeFeatureGroup)
      return [];
    if (!activeFeatureGroup.isComplex) {
      if (_.some(activeFeatureGroup.features)) {
        return activeFeatureGroup.features.filter(f => {
          let filter = (f.fabricatorData && f.fabricatorData.enabled);
          if (filter && (preset && preset.features)) {
            const found = _.some(preset.features, pf => _.isEqual(pf.featureDefinition_id, f.id));

            filter &= found;
          }
          return filter;
        });
      }
      return [];
    }

    if (_.some(activeFeatureGroup.mergedUniqueFeatures)) {
      return _.filter(activeFeatureGroup.mergedUniqueFeatures, f => {
        return _.some(activeFeatureGroup.subGroups, sg => {
          return _.some(_.filter(sg.features, _ => _.nakedName === f.nakedName),
            _ => _.fabricatorData && _.fabricatorData.enabled);
        })
      })
    }
    // complex group
    return [];
  }
)

export const getActiveFeatureGroupDisabledFeatures = createSelector(
  [getActiveFeatureGroup, selectFabricatorInclusionPreset],
  (activeFeatureGroup, preset) => {
    if (!activeFeatureGroup)
      return [];
    if (!activeFeatureGroup.isComplex) {
      if (_.some(activeFeatureGroup.features)) {
        return activeFeatureGroup.features.filter(f => {
          let filter = (!f.fabricatorData || !f.fabricatorData.enabled);
          if (filter && (preset && preset.features)) {
            let found = false;
            const size = _.size(preset.features);
            for (let i = 0; i < size; i++) {
              let pf = preset.features[i];
              if (_.isEqual(pf.featureDefinition_id, f.id)) {
                found = true;
              }
            }
            filter &= found;
          }
          return filter;
        });
      }
      return [];
    }

    if (_.some(activeFeatureGroup.mergedUniqueFeatures)) {
      return _.filter(activeFeatureGroup.mergedUniqueFeatures, f => {
        return _.some(activeFeatureGroup.subGroups, sg => {
          return _.some(_.filter(sg.features, _ => _.nakedName === f.nakedName),
            _ => !_.fabricatorData || !_.fabricatorData.enabled);
        })
      })
    }

    return [];
  }
)

export const getActiveFeatureGroupImageStyle = createSelector(
  selectActiveFeatureGroupName,
  (activeFeatureGroupName) => {
    if (activeFeatureGroupName) {
      const groupName = activeFeatureGroupName.toLowerCase();
      const round = /edge[s]*\s*profile/i.test(groupName) || /faucet[s]*/i.test(groupName);
      return round ? 'round' : 'rectangular';
    }
    return 'rectangular';
  }
);

export const getActiveFeatureGroupEnabledFilteredFeatures = createSelector(
  [getActiveFeatureGroupEnabledFeatures, selectComparableSearchTerm],
  (enabledFeatures, searchTerm) => {
    if (searchTerm.length > 0) {
      return enabledFeatures.filter(f => (f.fabricatorData && f.fabricatorData.displayName &&
        f.fabricatorData.displayName.toLowerCase().includes(searchTerm)) ||
        f.name.toLowerCase().includes(searchTerm));
    }
    return enabledFeatures;
  }
);

export const getActiveFeatureGroupDisabledFilteredFeatures = createSelector(
  [getActiveFeatureGroupDisabledFeatures, selectComparableSearchTerm],
  (disabledFeatures, searchTerm) => {
    if (searchTerm.length > 0) {
      return disabledFeatures.filter(f => (f.fabricatorData && f.fabricatorData.displayName &&
        f.fabricatorData.displayName.toLowerCase().includes(searchTerm)) ||
        f.name.toLowerCase().includes(searchTerm));
    }
    return disabledFeatures;
  }
);

export const getActiveFeaturesForDDL = createSelector(
  [selectFeatures],
  (features) => {
    let list = [];
    if (features) {
      let enabledFeatures = features.filter(f => (f.enabled && f.fabricatorData && f.fabricatorData.enabled));
      for (let featureIndex in enabledFeatures) {
        let feature = enabledFeatures[featureIndex];
        let name = feature.fabricatorData.displayName;
        if (!_.trim(name))
          name = feature.name;
        list.push({
          text: name,
          value: feature.fabricatorData.id
        });
      }
    }
    return list;
  }
)
